import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  Check,
  Download,
  Package,
  Pencil,
  Plus,
  Printer,
  Send,
  Undo2,
  } from 'lucide-react';
import {
  FREIGHT_MODE_LABELS,
  PAYMENT_METHOD_LABELS,
  PURCHASE_ORDER_AVULSO_REASON_LABELS,
  PURCHASE_ORDER_ORIGIN_LABELS,
  PURCHASE_ORDER_ORIGINS,
  PURCHASE_ORDER_RECEIVING_LABELS,
  PURCHASE_ORDER_RECEIVINGS,
  PURCHASE_ORDER_STATUS_LABELS,
  PURCHASE_ORDER_STATUSES,
  formatCents,
  formatQty,
  RECEIVING_SITUATION_LABELS,
  type Paginated,
  type PurchaseOrder,
  type PurchaseOrderSummary,
  type Receiving,
} from '@amai/contracts';
import { Link } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { blank, formatDate, formatDateTime, formatDocument } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SummaryCards } from '@/components/SummaryCards';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { Attachments } from '@/components/Attachments';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/Modal';
import { EmptyState, ErrorState, LoadingState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useCostCenters, useStockLocations, useSuppliers, useUnits } from '@/lib/queries';
import { AvulsoDrawer, RevisarDrawer, DaCotacaoDrawer } from './PedidoFormularios';

const STATUS_TONE: Record<string, BadgeKind> = {
  RASCUNHO: 'mute',
  EM_APROVACAO: 'warn',
  APROVADO: 'info',
  EMITIDO: 'info',
  CANCELADO: 'bad',
  ENCERRADO: 'ok',
};

const RECEIVING_SITUATION_TONE: Record<string, BadgeKind> = {
  EM_CONFERENCIA: 'info',
  RECEBIDO: 'ok',
  PARCIAL: 'warn',
  COM_DIVERGENCIA: 'bad',
  CANCELADO: 'mute',
};

const RECEIVING_TONE: Record<string, BadgeKind> = {
  NAO_RECEBIDO: 'bad',
  PARCIAL: 'warn',
  RECEBIDO: 'ok',
};

/**
 * Hook nomeado, não `useMutation` dentro de função comum.
 *
 * Chamar hook de dentro de um helper quebra a regra dos hooks e o lint acusa —
 * foi o que aconteceu na tela de Cotações.
 */
function useAcao(id: string | null, caminho: string, onSuccess: () => void) {
  return useMutation({
    mutationFn: (body: unknown) => api.post(`/purchase-orders/${id}/${caminho}`, body ?? {}),
    onSuccess,
  });
}

/**
 * Pedidos de compra.
 *
 * O caminho normal é emitir a partir da cotação decidida — itens e preços vão
 * sozinhos, e o pedido nasce amarrado à requisição que o originou. Cotação
 * dividida entre fornecedores gera UM PEDIDO POR FORNECEDOR: misturar dois
 * fornecedores no mesmo pedido não é documento que se mande para ninguém.
 *
 * O pedido avulso existe para a compra que a obra de fato tem (emergência,
 * balcão, fornecedor exclusivo) — mas exige motivo, justificativa e permissão
 * própria, para não virar a porta dos fundos do fluxo.
 */
export function PedidosPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const suppliers = useSuppliers();
  const costCenters = useCostCenters();
  const locations = useStockLocations();
  const units = useUnits();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [personId, setPersonId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [origin, setOrigin] = useState('');
  const [receiving, setReceiving] = useState('');
  const [locationId, setLocationId] = useState('');
  const [overdue, setOverdue] = useState('');
  const [minCents, setMinCents] = useState('');
  const [maxCents, setMaxCents] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [checked, setChecked] = useState<string[]>([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [daCotacao, setDaCotacao] = useState(false);
  const [avulso, setAvulso] = useState(false);
  const [revisando, setRevisando] = useState(false);
  const [confirmando, setConfirmando] = useState<'return' | 'close' | 'cancel' | null>(null);

  const filtros = {
    search: search || undefined,
    status: status || undefined,
    personId: personId || undefined,
    costCenterId: costCenterId || undefined,
    unitId: unitId || undefined,
    origin: origin || undefined,
    receiving: receiving || undefined,
    locationId: locationId || undefined,
    overdue: overdue || undefined,
    from: from || undefined,
    to: to || undefined,
    minCents: minCents ? String(Math.round(Number(minCents) * 100)) : undefined,
    maxCents: maxCents ? String(Math.round(Number(maxCents) * 100)) : undefined,
  };

  const listQuery = useQuery({
    queryKey: ['purchase-orders', { ...filtros, page, perPage }],
    queryFn: () => api.get<Paginated<PurchaseOrder>>('/purchase-orders', { ...filtros, page, perPage }),
  });

  const summaryQuery = useQuery({
    queryKey: ['purchase-orders', 'summary', filtros],
    queryFn: () => api.get<PurchaseOrderSummary>('/purchase-orders/summary', filtros),
  });

  const detailQuery = useQuery({
    queryKey: ['purchase-order', selectedId],
    queryFn: () => api.get<PurchaseOrder>(`/purchase-orders/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-order'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-requisitions'] });
    void queryClient.invalidateQueries({ queryKey: ['quotations'] });
  };

  const submitMutation = useAcao(selectedId, 'submit', refresh);
  const approveMutation = useAcao(selectedId, 'approve', refresh);
  const issueMutation = useAcao(selectedId, 'issue', refresh);
  const confirmMutation = useAcao(selectedId, 'confirm', refresh);
  const acaoComMotivo = useAcao(selectedId, confirmando ?? 'cancel', () => {
    setConfirmando(null);
    refresh();
  });

  const order = detailQuery.data;

  const maisFiltrosAtivos = [origin, receiving, locationId, unitId, overdue, minCents, maxCents].filter(
    Boolean,
  ).length;
  const temFiltro = Boolean(search || status || personId || costCenterId || from || to) || maisFiltrosAtivos > 0;

  const limparMais = () => {
    setOrigin('');
    setReceiving('');
    setLocationId('');
    setUnitId('');
    setOverdue('');
    setMinCents('');
    setMaxCents('');
    setPage(1);
  };
  const limparTudo = () => {
    setSearch('');
    setStatus('');
    setPersonId('');
    setCostCenterId('');
    setFrom('');
    setTo('');
    limparMais();
  };

  const alterna = (setter: (v: string) => void, atual: string, valor: string) => {
    setter(atual === valor ? '' : valor);
    setPage(1);
  };

  const resumo = summaryQuery.data;
  const cards = [
    {
      key: 'aberto',
      label: 'Em aberto',
      value: String(resumo?.open ?? 0),
      hint: 'Aguardando entrega',
      tone: 'info' as const,
      active: status === 'EMITIDO' && receiving === 'NAO_RECEBIDO',
      onClick: () => {
        const ligado = status === 'EMITIDO' && receiving === 'NAO_RECEBIDO';
        setStatus(ligado ? '' : 'EMITIDO');
        setReceiving(ligado ? '' : 'NAO_RECEBIDO');
        setPage(1);
      },
    },
    {
      key: 'entregues',
      label: 'Entregues',
      value: String(resumo?.delivered ?? 0),
      hint: 'Recebidos no estoque',
      tone: 'ok' as const,
      active: receiving === 'RECEBIDO',
      onClick: () => alterna(setReceiving, receiving, 'RECEBIDO'),
    },
    {
      key: 'parciais',
      label: 'Parcialmente recebidos',
      value: String(resumo?.partial ?? 0),
      hint: 'Com itens pendentes',
      tone: 'atencao' as const,
      active: receiving === 'PARCIAL',
      onClick: () => alterna(setReceiving, receiving, 'PARCIAL'),
    },
    {
      key: 'cancelados',
      label: 'Cancelados',
      value: String(resumo?.cancelled ?? 0),
      hint: 'No período',
      tone: 'ruim' as const,
      active: status === 'CANCELADO',
      onClick: () => alterna(setStatus, status, 'CANCELADO'),
    },
    {
      key: 'total',
      label: 'Valor total',
      value: formatCents(resumo?.totalCents ?? 0),
      hint: 'Pedidos emitidos',
      tone: 'neutro' as const,
    },
  ];

  const columns: Array<Column<PurchaseOrder>> = [
    {
      key: 'code',
      label: 'Código',
      sortable: true,
      strong: true,
      mono: true,
      width: 150,
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {row.code}
          {row.revision > 0 && (
            <span title={`${row.revision} alteração(ões) depois de emitido`} style={{ color: T.amber, fontSize: 11 }}>
              rev.{row.revision}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Data',
      sortable: true,
      width: 100,
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'supplier',
      label: 'Fornecedor',
      render: (row) => (
        <div>
          <div>{row.supplierName}</div>
          <div className="muted" style={{ fontSize: 11 }}>
            {row.supplierDocument ? formatDocument(row.supplierDocument) : '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'origin',
      label: 'Origem',
      width: 150,
      render: (row) =>
        row.origin === 'AVULSO' ? (
          <StatusBadge kind="warn">
            {row.avulsoReason ? PURCHASE_ORDER_AVULSO_REASON_LABELS[row.avulsoReason] : 'Avulso'}
          </StatusBadge>
        ) : (
          <div style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
            <div>{blank(row.quotationCode)}</div>
            <div className="muted">{blank(row.requisitionCode)}</div>
          </div>
        ),
    },
    {
      key: 'items',
      label: 'Itens',
      width: 80,
      render: (row) => `${row.items.length} ${row.items.length === 1 ? 'item' : 'itens'}`,
    },
    {
      key: 'totalCents',
      label: 'Valor total',
      sortable: true,
      right: true,
      mono: true,
      width: 120,
      render: (row) => formatCents(row.totalCents),
    },
    {
      key: 'expectedAt',
      label: 'Prazo de entrega',
      sortable: true,
      width: 140,
      render: (row) =>
        row.expectedAt ? (
          <span style={{ color: row.overdue ? T.red : undefined }}>
            {formatDate(row.expectedAt)}
            {row.overdue && (
              <AlertTriangle size={12} style={{ marginLeft: 4, verticalAlign: -1 }} aria-label="Atrasado" />
            )}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'status',
      label: 'Situação',
      width: 130,
      render: (row) => (
        <StatusBadge kind={STATUS_TONE[row.status]}>
          {PURCHASE_ORDER_STATUS_LABELS[row.status]}
        </StatusBadge>
      ),
    },
    {
      key: 'receiving',
      label: 'Recebimento',
      width: 130,
      render: (row) =>
        row.status === 'CANCELADO' ? (
          <span className="muted">—</span>
        ) : (
          <StatusBadge kind={RECEIVING_TONE[row.receiving]}>
            {PURCHASE_ORDER_RECEIVING_LABELS[row.receiving]}
          </StatusBadge>
        ),
    },
  ];

  const exportar = () => {
    const linhas = (listQuery.data?.data ?? []).map((row) =>
      [
        row.code,
        formatDate(row.createdAt),
        row.supplierName,
        row.supplierDocument ?? '',
        row.quotationCode ?? '',
        row.requisitionCode ?? '',
        String(row.items.length),
        (row.totalCents / 100).toFixed(2).replace('.', ','),
        row.expectedAt ? formatDate(row.expectedAt) : '',
        PURCHASE_ORDER_STATUS_LABELS[row.status],
        PURCHASE_ORDER_RECEIVING_LABELS[row.receiving],
      ].join(';'),
    );
    const csv = [
      'Código;Data;Fornecedor;CNPJ;Cotação;Requisição;Itens;Valor;Prazo;Situação;Recebimento',
      ...linhas,
    ].join('\n');
    // BOM para o Excel brasileiro abrir com acento certo.
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pedidos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <WorkspaceHeader
        title="Pedidos de compra"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" icon={Download} onClick={exportar} disabled={!listQuery.data?.data.length}>
              Exportar
            </Button>
            {(can('pedidos-compra:criar') || can('pedidos-compra:avulso')) && (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger className="btn btn--primary">
                  <Plus size={15} strokeWidth={2.2} aria-hidden />
                  Novo pedido
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content className="menu menu--choices" align="end" sideOffset={6}>
                    <DropdownMenu.Item
                      className="menu__item menu__item--choice"
                      disabled={!can('pedidos-compra:criar')}
                      onSelect={() => setDaCotacao(true)}
                    >
                      <strong>A partir de cotação</strong>
                      <span>O caminho normal: itens e preços vêm da proposta vencedora</span>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="menu__item menu__item--choice"
                      disabled={!can('pedidos-compra:avulso')}
                      onSelect={() => setAvulso(true)}
                    >
                      <strong>Pedido avulso</strong>
                      <span>Fora do fluxo: exige motivo, justificativa e aprovação</span>
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            )}
          </div>
        }
      />

      <div className="workspace__body">
        <SummaryCards cards={cards} loading={summaryQuery.isPending} />

        <FilterBar
          placeholder="Buscar por código, fornecedor, requisição, cotação ou item…"
          search={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          filters={[
            {
              key: 'status',
              label: 'Situação',
              value: status,
              onChange: (v) => {
                setStatus(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Todos' },
                ...PURCHASE_ORDER_STATUSES.map((s) => ({
                  value: s,
                  label: PURCHASE_ORDER_STATUS_LABELS[s],
                })),
              ],
            },
            {
              key: 'person',
              label: 'Fornecedor',
              value: personId,
              onChange: (v) => {
                setPersonId(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Todos' },
                ...(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.tradeName || s.name })),
              ],
            },
            {
              key: 'costCenter',
              label: 'Centro de custo',
              value: costCenterId,
              onChange: (v) => {
                setCostCenterId(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Todos' },
                ...(costCenters.data ?? []).map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` })),
              ],
            },
          ]}
          from={from}
          to={to}
          onFromChange={(v) => {
            setFrom(v);
            setPage(1);
          }}
          onToChange={(v) => {
            setTo(v);
            setPage(1);
          }}
          activeMoreFilters={maisFiltrosAtivos}
          onClearMore={limparMais}
          moreFilters={
            <>
              <label>
                <span>Origem</span>
                <select value={origin} onChange={(e) => { setOrigin(e.target.value); setPage(1); }}>
                  <option value="">Todas</option>
                  {PURCHASE_ORDER_ORIGINS.map((o) => (
                    <option key={o} value={o}>
                      {PURCHASE_ORDER_ORIGIN_LABELS[o]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Recebimento</span>
                <select value={receiving} onChange={(e) => { setReceiving(e.target.value); setPage(1); }}>
                  <option value="">Todos</option>
                  {PURCHASE_ORDER_RECEIVINGS.map((r) => (
                    <option key={r} value={r}>
                      {PURCHASE_ORDER_RECEIVING_LABELS[r]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Local de entrega</span>
                <select value={locationId} onChange={(e) => { setLocationId(e.target.value); setPage(1); }}>
                  <option value="">Todos</option>
                  {(locations.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} · {l.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Unidade</span>
                <select value={unitId} onChange={(e) => { setUnitId(e.target.value); setPage(1); }}>
                  <option value="">Todas</option>
                  {(units.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Entrega</span>
                <select value={overdue} onChange={(e) => { setOverdue(e.target.value); setPage(1); }}>
                  <option value="">Todas</option>
                  <option value="true">Só atrasados</option>
                </select>
              </label>
              <label>
                <span>Valor de (R$)</span>
                <input
                  type="number"
                  min={0}
                  value={minCents}
                  onChange={(e) => { setMinCents(e.target.value); setPage(1); }}
                />
              </label>
              <label>
                <span>Valor até (R$)</span>
                <input
                  type="number"
                  min={0}
                  value={maxCents}
                  onChange={(e) => { setMaxCents(e.target.value); setPage(1); }}
                />
              </label>
            </>
          }
        />

        {listQuery.isPending ? (
          <TableSkeleton />
        ) : listQuery.isError ? (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        ) : (
          <SmartDataTable
            caption="Pedidos de compra"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
            checkedIds={checked}
            onCheckedChange={setChecked}
            rowActions={(row) => (
              <a
                href={`/compras/pedidos/${row.id}/impressao`}
                target="_blank"
                rel="noreferrer"
                title="Imprimir o pedido"
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'inline-flex', padding: 4, color: T.inkSoft }}
              >
                <Printer size={15} />
              </a>
            )}
            emptyState={
              temFiltro ? (
                <NoResultsState onClear={limparTudo} />
              ) : (
                <EmptyState
                  title="Nenhum pedido de compra"
                  description="O pedido nasce da cotação decidida. Finalize uma cotação em Compras › Cotações e emita o pedido do fornecedor vencedor."
                />
              )
            }
          />
        )}

        <p className="muted" style={{ fontSize: 12, marginTop: 14, lineHeight: 1.6 }}>
          <strong style={{ color: T.inkSoft }}>Os pedidos são emitidos a partir de cotações aprovadas.</strong>{' '}
          Alterações, cancelamentos e recebimentos ficam registrados no histórico, garantindo rastreabilidade e
          controle do processo.
        </p>
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={order?.code ?? 'Pedido'}
        subtitle={order ? order.supplierName : undefined}
        tabs={[
          'Resumo',
          'Itens',
          'Condições',
          'Origem',
          'Recebimentos',
          'Documentos',
          'Aprovações',
          'Histórico',
        ]}
        footer={
          order && (
            <AcoesDoPedido
              order={order}
              can={can}
              trabalhando={
                submitMutation.isPending ||
                approveMutation.isPending ||
                issueMutation.isPending ||
                confirmMutation.isPending
              }
              onEnviar={() => submitMutation.mutate(undefined)}
              onAprovar={() => approveMutation.mutate(undefined)}
              onDevolver={() => setConfirmando('return')}
              onEmitir={() => issueMutation.mutate(undefined)}
              onConfirmar={() => confirmMutation.mutate(undefined)}
              onRevisar={() => setRevisando(true)}
              onEncerrar={() => setConfirmando('close')}
              onCancelar={() => setConfirmando('cancel')}
            />
          )
        }
      >
        {(aba) => {
          if (detailQuery.isPending) return <LoadingState label="Abrindo o pedido…" />;
          if (detailQuery.isError) return <ErrorState message={errorMessage(detailQuery.error)} />;
          if (!order) return null;

          if (aba === 1) return <AbaItens order={order} />;
          if (aba === 2) return <AbaCondicoes order={order} />;
          if (aba === 3) return <AbaOrigem order={order} />;
          if (aba === 4) return <AbaRecebimentos order={order} />;
          if (aba === 5)
            return <Attachments entity="PurchaseOrder" entityId={order.id} canEdit={can('pedidos-compra:editar')} />;
          if (aba === 6) return <AbaAprovacoes order={order} />;
          if (aba === 7) return <AbaHistorico orderId={order.id} />;
          return <AbaResumo order={order} />;
        }}
      </ContextPanel>

      {/* Montados só quando abertos: o formulário nasce limpo e não guarda
          estado de uma abertura anterior — foi o que causou o loop de render
          na tela de Cotações. */}
      {daCotacao && (
        <DaCotacaoDrawer
          onClose={() => setDaCotacao(false)}
          onCreated={(novo) => {
            setDaCotacao(false);
            setSelectedId(novo.id);
            refresh();
          }}
        />
      )}
      {avulso && (
        <AvulsoDrawer
          onClose={() => setAvulso(false)}
          onCreated={(novo) => {
            setAvulso(false);
            setSelectedId(novo.id);
            refresh();
          }}
        />
      )}
      {revisando && order && (
        <RevisarDrawer
          order={order}
          onClose={() => setRevisando(false)}
          onSaved={() => {
            setRevisando(false);
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmando !== null}
        title={
          confirmando === 'cancel'
            ? 'Cancelar o pedido'
            : confirmando === 'close'
              ? 'Encerrar o saldo'
              : 'Devolver para correção'
        }
        message={
          confirmando === 'cancel'
            ? 'O pedido não é apagado: fica cancelado, com motivo e autor, e o saldo volta para a fila de Compras.'
            : confirmando === 'close'
              ? 'O que ainda não chegou deixa de ser cobrado deste pedido e volta para a fila de Compras. Use quando o fornecedor não vai mais entregar o restante.'
              : 'O pedido volta para rascunho com o motivo, para o comprador corrigir e reenviar.'
        }
        confirmLabel={confirmando === 'cancel' ? 'Cancelar pedido' : 'Confirmar'}
        destructive={confirmando === 'cancel'}
        requireReason
        loading={acaoComMotivo.isPending}
        error={acaoComMotivo.error ? errorMessage(acaoComMotivo.error) : undefined}
        onClose={() => setConfirmando(null)}
        onConfirm={(reason) => acaoComMotivo.mutate({ reason })}
      />
    </>
  );
}

/* ------------------------------- ações ------------------------------------ */

/**
 * O que dá para fazer com este pedido, agora.
 *
 * Botão que o servidor recusaria não aparece: esconder é cortesia, mas a
 * recusa de verdade é no backend — as duas coisas juntas.
 */
function AcoesDoPedido({
  order,
  can,
  trabalhando,
  onEnviar,
  onAprovar,
  onDevolver,
  onEmitir,
  onConfirmar,
  onRevisar,
  onEncerrar,
  onCancelar,
}: {
  order: PurchaseOrder;
  can: (code: string) => boolean;
  trabalhando: boolean;
  onEnviar: () => void;
  onAprovar: () => void;
  onDevolver: () => void;
  onEmitir: () => void;
  onConfirmar: () => void;
  onRevisar: () => void;
  onEncerrar: () => void;
  onCancelar: () => void;
}) {
  const acoes: React.ReactNode[] = [];

  if (order.status === 'RASCUNHO' && can('pedidos-compra:editar')) {
    acoes.push(
      <Button key="submit" icon={Send} onClick={onEnviar} disabled={trabalhando}>
        Enviar para aprovação
      </Button>,
    );
  }
  if (order.status === 'EM_APROVACAO' && can('pedidos-compra:aprovar')) {
    acoes.push(
      <Button key="approve" icon={Check} onClick={onAprovar} disabled={trabalhando}>
        Aprovar
      </Button>,
      <Button key="return" variant="ghost" icon={Undo2} onClick={onDevolver}>
        Devolver
      </Button>,
    );
  }
  if (order.status === 'APROVADO' && can('pedidos-compra:emitir')) {
    acoes.push(
      <Button key="issue" icon={Send} onClick={onEmitir} disabled={trabalhando}>
        Emitir pedido
      </Button>,
    );
  }
  if (order.status === 'EMITIDO') {
    if (!order.confirmedAt && can('pedidos-compra:editar')) {
      acoes.push(
        <Button key="confirm" variant="ghost" icon={Check} onClick={onConfirmar} disabled={trabalhando}>
          Fornecedor confirmou
        </Button>,
      );
    }
    if (can('pedidos-compra:editar')) {
      acoes.push(
        <Button key="revise" variant="ghost" icon={Pencil} onClick={onRevisar}>
          Alterar
        </Button>,
        <Button key="close" variant="ghost" icon={Package} onClick={onEncerrar}>
          Encerrar saldo
        </Button>,
      );
    }
  }
  if (order.status !== 'CANCELADO' && order.status !== 'ENCERRADO' && can('pedidos-compra:excluir')) {
    acoes.push(
      <Button key="cancel" variant="ghost" icon={Ban} onClick={onCancelar}>
        Cancelar
      </Button>,
    );
  }

  acoes.push(
    <a
      key="print"
      href={`/compras/pedidos/${order.id}/impressao`}
      target="_blank"
      rel="noreferrer"
      className="btn btn--ghost"
      style={{ textDecoration: 'none' }}
    >
      <Printer size={15} /> Imprimir
    </a>,
  );

  return <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{acoes}</div>;
}

/* -------------------------------- abas ------------------------------------ */

function AbaResumo({ order }: { order: PurchaseOrder }) {
  return (
    <div>
      <StatTiles
        items={[
          { label: 'Valor total', value: formatCents(order.totalCents) },
          { label: 'Itens', value: String(order.items.length) },
          { label: 'Recebimento', value: PURCHASE_ORDER_RECEIVING_LABELS[order.receiving] },
          {
            label: 'Prazo',
            value: order.expectedAt ? (
              <span style={{ color: order.overdue ? T.red : undefined }}>{formatDate(order.expectedAt)}</span>
            ) : (
              '—'
            ),
          },
        ]}
      />

      {order.overdue && (
        <p style={{ color: T.red, fontSize: 12, marginTop: 10 }}>
          <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Prazo vencido com item ainda pendente.
        </p>
      )}

      {order.origin === 'AVULSO' && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: T.amberTint,
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          <strong>Pedido avulso — fora do fluxo de cotação.</strong>
          <div style={{ marginTop: 4 }}>
            Motivo:{' '}
            {order.avulsoReason ? PURCHASE_ORDER_AVULSO_REASON_LABELS[order.avulsoReason] : '—'}
          </div>
          {order.avulsoJustification && <div>Justificativa: {order.avulsoJustification}</div>}
        </div>
      )}

      <ReadField label="Fornecedor" value={order.supplierLegalName} />
      <ReadField label="CNPJ/CPF" value={order.supplierDocument ? formatDocument(order.supplierDocument) : '—'} />
      <ReadField label="Contato" value={blank(order.supplierPhone) + ' · ' + blank(order.supplierEmail)} />
      <ReadField label="Centro de custo" value={blank(order.costCenterName)} />
      <ReadField label="Unidade" value={blank(order.unitName)} />
      <ReadField label="Local de entrega" value={blank(order.locationName)} />
      {order.deliveryAddress && <ReadField label="Endereço de entrega" value={order.deliveryAddress} />}
      <ReadField label="Comprador" value={blank(order.buyerName)} />
      {order.notes && <ReadField label="Observações" value={order.notes} />}
      {order.supplierInstructions && (
        <ReadField label="Instruções ao fornecedor" value={order.supplierInstructions} />
      )}
      {order.cancelReason && <ReadField label="Motivo do cancelamento" value={order.cancelReason} />}
      {order.closeReason && <ReadField label="Motivo do encerramento" value={order.closeReason} />}
    </div>
  );
}

function AbaItens({ order }: { order: PurchaseOrder }) {
  return (
    <table className="mini-table">
      <caption className="sr-only">Itens do pedido</caption>
      <thead>
        <tr>
          <th>Item</th>
          <th className="num">Pedido</th>
          <th className="num">Recebido</th>
          <th className="num">Pendente</th>
          <th className="num">Unitário</th>
          <th className="num">Total</th>
        </tr>
      </thead>
      <tbody>
        {order.items.map((item) => (
          <tr key={item.id}>
            <td>
              <div>{item.description}</div>
              <div className="muted" style={{ fontSize: 11 }}>
                {[item.sku, item.brand, item.model].filter(Boolean).join(' · ') || item.unit}
              </div>
              {item.specification && (
                <div className="muted" style={{ fontSize: 11, fontStyle: 'italic' }}>
                  {item.specification}
                </div>
              )}
            </td>
            <td className="num">{formatQty(item.quantityMilli)}</td>
            <td className="num">{formatQty(item.receivedQuantityMilli)}</td>
            <td className="num" style={{ color: item.pendingQuantityMilli > 0 ? T.amber : T.green }}>
              {formatQty(item.pendingQuantityMilli)}
              {item.cancelledQuantityMilli > 0 && (
                <div className="muted" style={{ fontSize: 10 }}>
                  {formatQty(item.cancelledQuantityMilli)} cancelado
                </div>
              )}
            </td>
            <td className="num">{formatCents(item.unitPriceCents)}</td>
            <td className="num">{formatCents(item.totalCents)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={5}>Itens</td>
          <td className="num">{formatCents(order.itemsCents)}</td>
        </tr>
        {order.freightCents > 0 && (
          <tr>
            <td colSpan={5}>
              Frete {order.freightMode ? `(${FREIGHT_MODE_LABELS[order.freightMode]})` : ''}
            </td>
            <td className="num">{formatCents(order.freightCents)}</td>
          </tr>
        )}
        {order.discountCents > 0 && (
          <tr>
            <td colSpan={5}>Desconto</td>
            <td className="num">− {formatCents(order.discountCents)}</td>
          </tr>
        )}
        <tr>
          <td colSpan={5}>
            <strong>Total</strong>
          </td>
          <td className="num">
            <strong>{formatCents(order.totalCents)}</strong>
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function AbaCondicoes({ order }: { order: PurchaseOrder }) {
  return (
    <div>
      <ReadField label="Condição de pagamento" value={blank(order.paymentTerms)} />
      <ReadField
        label="Forma prevista"
        value={order.paymentMethod ? PAYMENT_METHOD_LABELS[order.paymentMethod] : '—'}
      />
      <ReadField label="Prazo de entrega" value={order.deliveryDays ? `${order.deliveryDays} dias` : '—'} />
      <ReadField label="Data prevista" value={order.expectedAt ? formatDate(order.expectedAt) : '—'} />
      <ReadField
        label="Frete"
        value={order.freightMode ? FREIGHT_MODE_LABELS[order.freightMode] : '—'}
      />
      <ReadField label="Valor do frete" value={formatCents(order.freightCents)} />
      <ReadField label="Transportadora" value={blank(order.carrierName)} />
      {order.freightNotes && <ReadField label="Observações do frete" value={order.freightNotes} />}
      <ReadField label="Desconto" value={formatCents(order.discountCents)} />
      <p className="muted" style={{ fontSize: 11, marginTop: 14, lineHeight: 1.6 }}>
        Registrar a condição aqui <strong>não</strong> cria conta a pagar. O Financeiro é alimentado
        no lançamento da nota, com base no que de fato foi recebido.
      </p>
    </div>
  );
}

function AbaOrigem({ order }: { order: PurchaseOrder }) {
  const elos = [
    { rotulo: 'Requisição', codigo: order.requisitionCode, href: '/compras/requisicoes' },
    { rotulo: 'Cotação', codigo: order.quotationCode, href: '/compras/cotacoes' },
    { rotulo: 'Pedido', codigo: order.code, href: null },
  ].filter((elo) => elo.codigo);

  return (
    <div>
      <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        {order.origin === 'AVULSO'
          ? 'Pedido avulso: não nasceu de cotação. O motivo fica no Resumo e na auditoria.'
          : 'A cadeia do documento, do pedido de material até a compra.'}
      </p>
      <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {elos.map((elo, i) => (
          <li key={elo.rotulo} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: i === elos.length - 1 ? T.mare : T.line,
                  marginTop: 6,
                }}
              />
              {i < elos.length - 1 && <span style={{ width: 1, flex: 1, minHeight: 26, background: T.line }} />}
            </div>
            <div style={{ paddingBottom: 14 }}>
              <div className="muted" style={{ fontSize: 11 }}>
                {elo.rotulo}
              </div>
              {elo.href ? (
                <a href={elo.href} style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                  {elo.codigo}
                </a>
              ) : (
                <strong style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{elo.codigo}</strong>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Como `string`: as rotas de módulo não entram no mapa tipado do router. */
function caminhoDoRecebimento(r: Receiving): string {
  return r.status === 'RASCUNHO' || r.status === 'EM_CONFERENCIA'
    ? `/compras/recebimentos/${r.id}/conferir`
    : '/compras/recebimentos';
}

function AbaRecebimentos({ order }: { order: PurchaseOrder }) {
  const recebidos = order.items.reduce((s, i) => s + i.receivedQuantityMilli, 0);
  const pedidos = order.items.reduce((s, i) => s + i.quantityMilli, 0);
  const query = useQuery({
    queryKey: ['receivings', { purchaseOrderId: order.id }],
    queryFn: () => api.get<Paginated<Receiving>>('/receivings', { purchaseOrderId: order.id, perPage: 50 }),
  });

  return (
    <div>
      <StatTiles
        items={[
          { label: 'Pedido', value: formatQty(pedidos) },
          { label: 'Recebido', value: formatQty(recebidos) },
          { label: 'Pendente', value: formatQty(Math.max(pedidos - recebidos, 0)) },
        ]}
      />
      {query.isPending && <LoadingState label="Carregando recebimentos…" />}
      {query.isError && <ErrorState message={errorMessage(query.error)} />}
      {query.data && query.data.data.length === 0 && (
        <EmptyState
          title="Nada recebido ainda"
          description="Quando o material chegar, abra o recebimento em Compras › Recebimentos. O que for aceito na conferência atualiza o saldo de cada linha."
        />
      )}
      {query.data?.data.map((r) => (
        <Link
          key={r.id}
          to={caminhoDoRecebimento(r)}
          className="card"
          style={{ display: 'block', padding: 12, marginBottom: 8, textDecoration: 'none', color: 'inherit' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <strong className="mono" style={{ fontSize: 13 }}>
              {r.code}
            </strong>
            <StatusBadge kind={RECEIVING_SITUATION_TONE[r.situation]}>{RECEIVING_SITUATION_LABELS[r.situation]}</StatusBadge>
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            {formatDate(r.receivedAt)} · {r.itemCount} {r.itemCount === 1 ? 'item' : 'itens'} · {formatCents(r.totalCents)}
            {r.receivedByName ? ` · ${r.receivedByName}` : ''}
          </div>
        </Link>
      ))}
      <p className="muted" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.6 }}>
        O andamento é calculado <strong>por item</strong>: o pedido só fica recebido quando todas as
        linhas fecham. Só a quantidade ACEITA na conferência conta — o que foi recusado continua pendente.
      </p>
    </div>
  );
}

function AbaAprovacoes({ order }: { order: PurchaseOrder }) {
  return (
    <div>
      <ReadField label="Situação" value={PURCHASE_ORDER_STATUS_LABELS[order.status]} />
      <ReadField label="Aprovado em" value={order.approvedAt ? formatDateTime(order.approvedAt) : '—'} />
      <ReadField
        label="Alçada de quem aprovou"
        value={order.approvedUpToCents !== null ? `até ${formatCents(order.approvedUpToCents)}` : 'sem teto configurado'}
      />
      {order.returnedAt && (
        <>
          <ReadField label="Devolvido em" value={formatDateTime(order.returnedAt)} />
          <ReadField label="Motivo da devolução" value={blank(order.returnReason)} />
        </>
      )}
      <ReadField label="Emitido em" value={order.issuedAt ? formatDateTime(order.issuedAt) : '—'} />
      <ReadField
        label="Confirmado pelo fornecedor"
        value={order.confirmedAt ? formatDateTime(order.confirmedAt) : '—'}
      />

      {order.revisions.length > 0 && (
        <>
          <h4 style={{ fontSize: 12, textTransform: 'uppercase', color: T.inkSoft, marginTop: 20 }}>
            Alterações depois de emitido
          </h4>
          {order.revisions.map((rev) => (
            <div key={rev.id} style={{ borderLeft: `2px solid ${T.line}`, paddingLeft: 10, marginTop: 10 }}>
              <div style={{ fontSize: 12 }}>
                <strong>Revisão {rev.number}</strong> · {formatCents(rev.totalBeforeCents)} →{' '}
                {formatCents(rev.totalAfterCents)}
                {rev.reapproved && (
                  <StatusBadge kind="warn">
                    <span style={{ marginLeft: 6 }}>voltou para aprovação</span>
                  </StatusBadge>
                )}
              </div>
              <div className="muted" style={{ fontSize: 11 }}>
                {blank(rev.changedByName)} · {formatDateTime(rev.createdAt)}
              </div>
              <div style={{ fontSize: 12, marginTop: 2 }}>{rev.reason}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function AbaHistorico({ orderId }: { orderId: string }) {
  const query = useQuery({
    queryKey: ['purchase-order', orderId, 'timeline'],
    queryFn: () =>
      api.get<Array<{ id: string; action: string; userName: string | null; at: string; reason: string | null }>>(
        `/purchase-orders/${orderId}/timeline`,
      ),
  });

  if (query.isPending) return <LoadingState label="Carregando o histórico…" />;
  if (query.isError) return <ErrorState message={errorMessage(query.error)} />;
  if (query.data.length === 0) {
    return <p className="muted" style={{ fontSize: 13 }}>Nada registrado ainda.</p>;
  }

  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {query.data.map((linha) => (
        <li key={linha.id} style={{ borderLeft: `2px solid ${T.line}`, paddingLeft: 12, paddingBottom: 14 }}>
          <div style={{ fontSize: 12 }}>{blank(linha.userName)}</div>
          <div className="muted" style={{ fontSize: 11 }}>
            {formatDateTime(linha.at)}
          </div>
          {linha.reason && <div style={{ fontSize: 12, marginTop: 2 }}>{linha.reason}</div>}
        </li>
      ))}
    </ol>
  );
}
